/**
 * **Themed generator presets** (PRD #837, slice 5).
 *
 * A campaign dungeon node that rolls its map declares one of these instead of
 * an authored map id: which procedural generator family rolls the layout,
 * which module kit paints it, the envelope that family promises, which monster
 * family populates it, how many floors the node runs, and optionally the boss
 * the population plan anchors in the boss landmark room.
 *
 * This is pure content the way `classes/` and `items/` are: nothing here reads
 * a world, an entity, a session, or a socket. The campaign graph (slice 2)
 * references presets by id; the guard below makes a preset that names a kit,
 * family, or boss that does not exist an authoring failure at load rather than
 * a run that generates wrong.
 *
 * The first preset is Despise level 1. It rides the compact cave family and
 * the Shame kit today because themed Despise module art is content owned in
 * parallel (see the PRD's content-input note); when that kit lands, swapping
 * `structureKitId` here re-themes the node without touching a consumer.
 */

import { dungeonStructureKit } from '../dungeonStructureKit';
import {
  SHAME_DUNGEON_HEIGHT,
  SHAME_DUNGEON_WIDTH,
} from '../shameDungeon';
import {
  arpgMonsterFamily,
  type ArpgMonsterFamilyId,
  type PlanArpgPopulationOptions,
} from '../arpgPopulation';
import { arpgMonsterArchetype, type ArpgMonsterArchetypeId } from '../arpgMonsters';
import { DESPISE_BOSS_ARCHETYPE_ID } from './campaign';

/** Every declared preset. Ids are content identity and never change. */
export type ArpgGeneratorPresetId = 'despise-level-1';

/** The procedural generator families a preset may name. */
export type ArpgGeneratorFamilyId = 'shame' | 'classic-stone';

export interface ArpgGeneratorPreset {
  id: ArpgGeneratorPresetId;
  label: string;
  /** Which procedural family rolls the layout; matches the blueprint's own id. */
  generatorId: ArpgGeneratorFamilyId;
  /** The module kit (structure kit) the compiled layout is painted with. */
  structureKitId: string;
  /** The envelope the generator family promises, in tiles. */
  size: { widthTiles: number; heightTiles: number };
  /** Which monster family wears the floor's pack roles. */
  monsterFamily: ArpgMonsterFamilyId;
  /** How many floors the node runs before its final room. */
  floorCount: number;
  /** The boss the population plan anchors in the boss landmark room. */
  bossArchetype?: ArpgMonsterArchetypeId;
}

export const DESPISE_LEVEL_1_PRESET: ArpgGeneratorPreset = {
  id: 'despise-level-1',
  label: 'Despise Level 1',
  generatorId: 'shame',
  structureKitId: 'shame-v1',
  size: { widthTiles: SHAME_DUNGEON_WIDTH, heightTiles: SHAME_DUNGEON_HEIGHT },
  monsterFamily: 'ratmen',
  floorCount: 1,
  // The one coordination point with the campaign graph: the slay-boss
  // objective on despise-1 keys on this same exported constant.
  bossArchetype: DESPISE_BOSS_ARCHETYPE_ID,
};

export const ARPG_GENERATOR_PRESETS: readonly ArpgGeneratorPreset[] = [
  DESPISE_LEVEL_1_PRESET,
];

/** Look up a preset. An unknown id is null rather than a throw. */
export function arpgGeneratorPreset(id: string): ArpgGeneratorPreset | null {
  return (
    ARPG_GENERATOR_PRESETS.find((preset) => preset.id === id) ?? null
  );
}

/**
 * The population-planner options this preset declares: its monster family and,
 * when it names one, its boss. The blueprint and seed stay the caller's.
 */
export function presetPopulationOptions(
  preset: ArpgGeneratorPreset,
): Pick<PlanArpgPopulationOptions, 'family' | 'boss'> {
  return {
    family: preset.monsterFamily,
    ...(preset.bossArchetype ? { boss: preset.bossArchetype } : {}),
  };
}

/**
 * The load-time guard over the names that have a registry to resolve against:
 * the structure kit, the monster family, and the boss archetype, plus the
 * floor count's shape. A typo in any of them is an authoring failure here
 * rather than a floor that silently generates unthemed or unpopulated.
 * `generatorId` and `size` are declarations with no registry today; the preset
 * tests pin them against the generator family's own exported constants.
 */
function assertPresetResolves(preset: ArpgGeneratorPreset): void {
  if (!dungeonStructureKit(preset.structureKitId)) {
    throw new Error(
      `generator preset '${preset.id}' names unknown structure kit '${preset.structureKitId}'`,
    );
  }
  if (!arpgMonsterFamily(preset.monsterFamily)) {
    throw new Error(
      `generator preset '${preset.id}' names unknown monster family '${preset.monsterFamily}'`,
    );
  }
  if (!Number.isSafeInteger(preset.floorCount) || preset.floorCount < 1) {
    throw new Error(
      `generator preset '${preset.id}' declares an illegal floor count ${preset.floorCount}`,
    );
  }
  if (preset.bossArchetype && !arpgMonsterArchetype(preset.bossArchetype)) {
    throw new Error(
      `generator preset '${preset.id}' names unknown boss archetype '${preset.bossArchetype}'`,
    );
  }
}

for (const preset of ARPG_GENERATOR_PRESETS) assertPresetResolves(preset);
